import React, { useState, useEffect, useCallback } from 'react';
import { NewsService } from '../../services/NewsService';
import { NewsCard } from './NewsCard';
import { logger } from '../../utils/logger';
import { RefreshCw } from 'lucide-react';
import type { NewsArticle, NewsCategory } from '../../types/market';

interface MarketNewsProps {
  limit?: number;
  autoRefresh?: boolean;
  refreshInterval?: number;
  showFilters?: boolean;
  compact?: boolean;
}

const NEWS_CATEGORIES: NewsCategory[] = [
  { id: 'all',         name: 'All',         topics: [] },
  { id: 'markets',     name: 'Markets',     topics: ['financial_markets', 'stock_market'] },
  { id: 'economy',     name: 'Economy',     topics: ['economy_fiscal', 'economy_monetary', 'economy_macro'] },
  { id: 'earnings',    name: 'Earnings',    topics: ['earnings'] },
  { id: 'crypto',      name: 'Crypto',      topics: ['blockchain'] },
  { id: 'commodities', name: 'Commodities', topics: ['energy', 'commodities'] },
];

const SENTIMENTS = [
  { id: 'all',      label: 'All',      color: '#94a3b8', bg: 'rgba(148,163,184,0.1)', border: 'rgba(148,163,184,0.2)' },
  { id: 'positive', label: 'Positive', color: '#10b981', bg: 'rgba(16,185,129,0.1)',  border: 'rgba(16,185,129,0.25)'  },
  { id: 'neutral',  label: 'Neutral',  color: '#94a3b8', bg: 'rgba(148,163,184,0.1)', border: 'rgba(148,163,184,0.2)' },
  { id: 'negative', label: 'Negative', color: '#ef4444', bg: 'rgba(239,68,68,0.1)',   border: 'rgba(239,68,68,0.25)'  },
];

function timeAgo(date: Date): string {
  const mins = Math.floor((Date.now() - date.getTime()) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  return `${Math.floor(mins / 60)}h ago`;
}

export const MarketNews: React.FC<MarketNewsProps> = ({
  limit = 10,
  autoRefresh = true,
  refreshInterval = 5,
  showFilters = true,
  compact = false,
}) => {
  const [articles, setArticles]           = useState<NewsArticle[]>([]);
  const [loading, setLoading]             = useState(true);
  const [spinning, setSpinning]           = useState(false);
  const [error, setError]                 = useState<string | null>(null);
  const [lastUpdated, setLastUpdated]     = useState<Date | null>(null);
  const [selectedCategory, setCategory]   = useState('all');
  const [sentimentFilter, setSentiment]   = useState('all');
  const [newsService]                     = useState(() => new NewsService());

  const fetchNews = useCallback(async () => {
    try {
      setError(null);
      let items: NewsArticle[] = [];
      if (selectedCategory === 'all') {
        items = await newsService.getMarketNews(limit);
      } else {
        const cat = NEWS_CATEGORIES.find(c => c.id === selectedCategory);
        items = cat?.topics.length
          ? await newsService.getTopicNews(cat.topics, limit)
          : await newsService.getMarketNews(limit);
      }
      if (sentimentFilter !== 'all') {
        items = items.filter(a => a.sentiment === sentimentFilter);
      }
      setArticles(items);
      setLastUpdated(new Date());
    } catch (err: any) {
      logger.error('Error fetching news', err);
      setError(err?.message ?? 'Failed to load news. Please try again later.');
    } finally {
      setLoading(false);
      setSpinning(false);
    }
  }, [newsService, limit, selectedCategory, sentimentFilter]);

  useEffect(() => { fetchNews(); }, [fetchNews]);

  useEffect(() => {
    if (!autoRefresh) return;
    const iv = setInterval(fetchNews, refreshInterval * 60 * 1000);
    return () => clearInterval(iv);
  }, [autoRefresh, refreshInterval, fetchNews]);

  const handleRefresh = () => {
    setSpinning(true);
    fetchNews();
  };

  /* pill button factory */
  const FilterPill: React.FC<{ active: boolean; label: string; color: string; bg: string; border: string; onClick: () => void }> =
    ({ active, label, color, bg, border, onClick }) => (
      <button
        onClick={onClick}
        style={{
          padding: '0.25rem 0.75rem', borderRadius: 99,
          fontSize: '0.72rem', fontWeight: 700,
          background: active ? bg : 'transparent',
          border: `1px solid ${active ? border : '#1e2d45'}`,
          color: active ? color : '#334155',
          cursor: 'pointer', transition: 'all 0.18s',
          whiteSpace: 'nowrap',
        }}
        onMouseEnter={e => { if (!active) { e.currentTarget.style.borderColor = '#2a3a55'; e.currentTarget.style.color = '#64748b'; } }}
        onMouseLeave={e => { if (!active) { e.currentTarget.style.borderColor = '#1e2d45'; e.currentTarget.style.color = '#334155'; } }}
      >
        {label}
      </button>
    );

  /* ── Loading skeleton ── */
  if (loading && articles.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? '0.5rem' : '0.75rem' }}>
        {Array.from({ length: compact ? 6 : 4 }).map((_, i) => (
          <div key={i} style={{ display: 'flex', gap: '0.625rem', alignItems: 'flex-start' }}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div className="skeleton" style={{ height: 13, width: '85%', borderRadius: 4 }} />
              <div className="skeleton" style={{ height: 11, width: '60%', borderRadius: 4 }} />
              {!compact && <div className="skeleton" style={{ height: 10, width: '40%', borderRadius: 4 }} />}
            </div>
            <div className="skeleton" style={{ width: 44, height: 20, borderRadius: 99, flexShrink: 0 }} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>

      {/* ── Filters ── */}
      {showFilters && !compact && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
          {/* Category */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', alignItems: 'center' }}>
            <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: '0.07em', marginRight: 4 }}>
              Category
            </span>
            {NEWS_CATEGORIES.map(cat => (
              <FilterPill
                key={cat.id}
                active={selectedCategory === cat.id}
                label={cat.name}
                color="#60a5fa"
                bg="rgba(59,130,246,0.12)"
                border="rgba(59,130,246,0.3)"
                onClick={() => setCategory(cat.id)}
              />
            ))}
          </div>
          {/* Sentiment */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', alignItems: 'center' }}>
            <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: '0.07em', marginRight: 4 }}>
              Sentiment
            </span>
            {SENTIMENTS.map(s => (
              <FilterPill
                key={s.id}
                active={sentimentFilter === s.id}
                label={s.label}
                color={s.color}
                bg={s.bg}
                border={s.border}
                onClick={() => setSentiment(s.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Meta row ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {lastUpdated && (
          <span style={{ fontSize: '0.7rem', color: '#1e3a5f', fontWeight: 500 }}>
            Updated {timeAgo(lastUpdated)}
          </span>
        )}
        <button
          onClick={handleRefresh}
          disabled={spinning}
          aria-label="Refresh news"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 7, background: 'transparent', border: '1px solid #1e2d45', cursor: spinning ? 'not-allowed' : 'pointer', color: '#334155', transition: 'all 0.18s', marginLeft: 'auto' }}
          onMouseEnter={e => { e.currentTarget.style.background = '#1a2235'; e.currentTarget.style.color = '#60a5fa'; e.currentTarget.style.borderColor = 'rgba(59,130,246,0.3)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#334155'; e.currentTarget.style.borderColor = '#1e2d45'; }}
        >
          <RefreshCw size={13} strokeWidth={2} style={{ animation: spinning ? 'spin 0.7s linear infinite' : 'none' }} />
        </button>
      </div>

      {/* ── Error ── */}
      {error && (
        <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#f87171', display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01" strokeLinecap="round"/></svg>
          {error}
        </div>
      )}

      {/* ── Empty ── */}
      {articles.length === 0 && !loading && !error && (
        <div style={{ textAlign: 'center', padding: '2.5rem 1rem' }}>
          <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(148,163,184,0.08)', border: '1px solid rgba(148,163,184,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 0.75rem' }}>
            <svg width="18" height="18" fill="none" stroke="#475569" strokeWidth="1.75" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9.5a2.5 2.5 0 00-2.5-2.5H15"/></svg>
          </div>
          <p style={{ fontSize: '0.875rem', color: '#475569', fontWeight: 600, marginBottom: '0.25rem' }}>No articles found</p>
          <p style={{ fontSize: '0.78rem', color: '#1e3a5f' }}>Try adjusting filters or refresh</p>
        </div>
      )}

      {/* ── Articles ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? 0 : '0.625rem' }}>
        {articles.map((a, i) => (
          <NewsCard
            key={`${a.url}-${i}`}
            article={a}
            showSentiment={!compact}
            compact={compact}
          />
        ))}
      </div>

      {/* ── Auto-refresh indicator ── */}
      {autoRefresh && lastUpdated && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, paddingTop: '0.5rem', borderTop: '1px solid #0f1929' }}>
          <div className="live-dot" style={{ width: 6, height: 6 }} />
          <span style={{ fontSize: '0.7rem', color: '#1e3a5f', fontWeight: 500 }}>
            Auto-refreshing every {refreshInterval}m
          </span>
        </div>
      )}
    </div>
  );
};
